define('presentation/reactivation/facades/reactivationFacade',[
    'businessServices/creditCards/creditCardTypes',
    'businessServices/specifications/validCreditCardSpecification',
    'common/collections/collectionSorter',
    'common/promises/promiseFactory',
    'common/time/date',
    'constants/creditCardResponseConstants',
    'constants/creditCardResponseEnumerations',
    'entities/paymentMethodEntity',
    'persistence/dataProviders/accountDataProvider',
    'persistence/dataProviders/paymentMethodDataProvider',
    'persistence/repositories/payBalanceRepository',
    'persistence/repositories/paymentMethodRepository',
    'presentation/common/dateTimeValue',
    'presentation/common/paymentMethod/presentationObjects/paymentMethodPresentationObject',
], function () {
    const AccountDataProviderConstructor = require('persistence/dataProviders/accountDataProvider');
    const _accountDataProvider = new AccountDataProviderConstructor();

    const CreditCardConstructor = require('businessServices/creditCards/creditCardTypes');
    const _creditCard = new CreditCardConstructor();

    const CollectionSorterConstructor = require('common/collections/collectionSorter');
    const _collectionSorter = new CollectionSorterConstructor();

    const DateConstructor = require('common/time/date');
    const _date = new DateConstructor();

    const PayBalanceRepositoryConstructor = require('persistence/repositories/payBalanceRepository');
    const _payBalanceRepository = new PayBalanceRepositoryConstructor();

    const PaymentMethodDataProvider = require('persistence/dataProviders/paymentMethodDataProvider');
    const _paymentMethodDataProvider = new PaymentMethodDataProvider();

    const PaymentMethodRepositoryConstructor = require('persistence/repositories/paymentMethodRepository');
    const _paymentMethodRepository = new PaymentMethodRepositoryConstructor();

    const PromiseFactoryConstructor = require('common/promises/promiseFactory');
    const _promiseFactory = new PromiseFactoryConstructor();

    const ValidCreditCardSpecification = require('businessServices/specifications/validCreditCardSpecification');
    const _validCreditCardSpecification = new ValidCreditCardSpecification();

    const PaymentMethodPresentationObjectConstructor = require('presentation/common/paymentMethod/presentationObjects/paymentMethodPresentationObject');
    const PaymentMethodEntityConstructor = require('entities/paymentMethodEntity');

    const _creditCardResponse = require('constants/creditCardResponseConstants');

    let _webSocketApp = null;

    const _setDefaultPaymentMethod = (paymentMethodId) => {
        const paymentMethodEntity = new PaymentMethodEntityConstructor();
        paymentMethodEntity.paymentMethodId = paymentMethodId;

        return _paymentMethodRepository.setDefault(paymentMethodEntity);
    };

    const _addPaymentMethodAndMakeDefault = (paymentMethod) => {
        const paymentMethodEntity = new PaymentMethodEntityConstructor();
        paymentMethodEntity.isDefault = true;
        paymentMethodEntity.cardHolderName = paymentMethod.cardHolderName.trim();
        paymentMethodEntity.cardType = paymentMethod.cardType.trim();
        paymentMethodEntity.creditCardNumber = _creditCard.unformatCreditCard(paymentMethod.creditCardNumber);
        paymentMethodEntity.cvvSecurityCode = paymentMethod.cvvSecurityCode.trim();
        paymentMethodEntity.expirationDate = _date.getExpirationDate(paymentMethod.expirationYear, paymentMethod.expirationMonth);
        paymentMethodEntity.country = paymentMethod.country;
        paymentMethodEntity.postalCode = paymentMethod.postalCode.trim();

        return _paymentMethodRepository.add(paymentMethodEntity);
    };

    const _addPaymentMethodAndMakePayment = (paymentMethod, paymentAmount) => {
        return _promiseFactory.defer((deferredObject) => {
            const paymentMethodEntity = new PaymentMethodEntityConstructor();
            paymentMethodEntity.cardHolderName = paymentMethod.cardHolderName.trim();
            paymentMethodEntity.cardType = paymentMethod.cardType.trim();
            paymentMethodEntity.creditCardNumber = _creditCard.unformatCreditCard(paymentMethod.creditCardNumber);
            paymentMethodEntity.cvvSecurityCode = paymentMethod.cvvSecurityCode.trim();
            paymentMethodEntity.expirationDate = _date.getExpirationDate(paymentMethod.expirationYear, paymentMethod.expirationMonth);
            paymentMethodEntity.isDefault = false;
            paymentMethodEntity.country = paymentMethod.country;
            paymentMethodEntity.postalCode = paymentMethod.postalCode.trim();

            _payBalanceRepository.addPaymentMethodAndMakePayment(paymentMethodEntity, paymentAmount)
                .done(deferredObject.resolve)
                .fail(deferredObject.reject);
        });
    };

    const _makePayment = (paymentMethodId, paymentAmount) => {
        return _promiseFactory.defer((deferredObject) => {
            _payBalanceRepository.applyPayment(paymentMethodId, paymentAmount)
                .done(deferredObject.resolve)
                .fail(deferredObject.reject);
        });
    };

    const _reactivateAccount = (reactivationData) => {
        _promiseFactory.timeoutMilliSeconds = 90000;
        return _promiseFactory.defer((deferredObject) => {
            const socketParams = {
                planId: reactivationData.planId,
                telephonyPackageId: reactivationData.telephonyPackageId,
                liveReceptionistPackageId: reactivationData.liveReceptionistPackageId,
                webChatPackageId: reactivationData.webChatPackageId,
                outboundCallingPackageId: reactivationData.outboundCallingPackageId,
                paymentMethodId: reactivationData.paymentMethodId,
                newPaymentMethod: {
                    cardHolderName: reactivationData.newPaymentMethod ? reactivationData.newPaymentMethod.cardHolderName : "",
                    cardNumber: reactivationData.newPaymentMethod ? reactivationData.newPaymentMethod.creditCardNumber : "",
                    expirationDate: reactivationData.newPaymentMethod ? reactivationData.newPaymentMethod.expirationDate : "",
                    cvvSecurityCode: reactivationData.newPaymentMethod ? reactivationData.newPaymentMethod.cvvSecurityCode : "",
                    postalCode: reactivationData.newPaymentMethod ? reactivationData.newPaymentMethod.postalCode : "",
                    country: reactivationData.newPaymentMethod ? reactivationData.newPaymentMethod.country : "",
                    cardType: reactivationData.newPaymentMethod ? reactivationData.newPaymentMethod.cardType : ""
                },
                reservedPhoneNumbers: [],
                removedPhoneConnectors: []
            };

            _webSocketApp.send("reactivateAccount", socketParams, (result) => {
                deferredObject.resolve(result);
            });
        });
    };

    const _getBalanceInformation = () => {
        return _promiseFactory.defer((promise) => {
            _accountDataProvider.getBalanceInformation()
                .fail(promise.reject)
                .done((balanceInformation) => {
                    promise.resolve(balanceInformation.pastDueBalance);
                });
        });
    };

    const _getReactivationInformation = () => {
        return _promiseFactory.defer((deferredObject) => {
            _webSocketApp.send("getReactivationInfo", {},  (result) => {
                deferredObject.resolve(result);
            });
        });
    };

    const _getAccountInformation = () => {
        return _promiseFactory.defer((deferredObject) => {
            _accountDataProvider.getAccount()
                .done(deferredObject.resolve)
                .fail(deferredObject.reject);
        });
    };

    const _getAllPaymentMethods = () => {
        return _promiseFactory.defer((deferredObject) => {
            _paymentMethodDataProvider.getAll()
                .fail(deferredObject.reject)
                .done((paymentMethods) => {
                    const paymentMethodsFormatted = paymentMethods.map((currentPaymentMethod) => {
                        return _buildPaymentMethodPresentationObject(currentPaymentMethod);
                    });
                    _collectionSorter.sort(paymentMethodsFormatted, "description", true);
                    deferredObject.resolve(paymentMethodsFormatted);
                });
        });
    };

    const _buildPaymentMethodPresentationObject = (currentPaymentMethod) => {
        const paymentMethodPresentationObject = new PaymentMethodPresentationObjectConstructor();
        paymentMethodPresentationObject.paymentMethodId = currentPaymentMethod.paymentMethodId;
        paymentMethodPresentationObject.cardHolderName = currentPaymentMethod.cardHolderName;
        paymentMethodPresentationObject.cardType = currentPaymentMethod.cardType;
        paymentMethodPresentationObject.expirationDate = currentPaymentMethod.expirationDate;
        paymentMethodPresentationObject.isDefault(currentPaymentMethod.isDefault);
        paymentMethodPresentationObject.isExpired(_isPaymentMethodExpired(currentPaymentMethod.expirationDate));
        paymentMethodPresentationObject.country = currentPaymentMethod.country;
        paymentMethodPresentationObject.postalCode = currentPaymentMethod.postalCode;
        const creditCard = _creditCard.getCreditCardLongDisplay(currentPaymentMethod.cardType, currentPaymentMethod.lastFourDigits, currentPaymentMethod.expirationDate);
        paymentMethodPresentationObject.description = creditCard.longDisplay;
        return paymentMethodPresentationObject;
    };

    const _getAddPaymentMethodAllowed = () => {
        return _promiseFactory.defer((deferredObject) => {
            _paymentMethodDataProvider.getAddPaymentMethodAttempts()
                .fail(deferredObject.reject)
                .done((attemptsStatus) => {
                    deferredObject.resolve(attemptsStatus.status === _creditCardResponse.success);
                });
        });
    };

    const _isPaymentMethodExpired = (expirationDate) => {
        return _validCreditCardSpecification.isCreditCardExpired(expirationDate);
    };

    const _init = () => {

        const WebSocketAppConstructor = require('persistence/webSocket/webSocketApp');
        _webSocketApp = new WebSocketAppConstructor();
        _webSocketApp.init("reactivation");

        _paymentMethodDataProvider.init();
        _paymentMethodRepository.init();
        _payBalanceRepository.init();
    };

    return function () {
        let self = this;

        self.addPaymentMethodAndMakeDefault = _addPaymentMethodAndMakeDefault;
        self.addPaymentMethodAndMakePayment = _addPaymentMethodAndMakePayment;
        self.makePayment = _makePayment;
        self.buildPaymentMethodPresentationObject = _buildPaymentMethodPresentationObject;
        self.getAddPaymentMethodAllowed = _getAddPaymentMethodAllowed;
        self.getAllPaymentMethods = _getAllPaymentMethods;
        self.getBalanceInformation = _getBalanceInformation;
        self.getAccountInformation = _getAccountInformation;
        self.getReactivationInformation = _getReactivationInformation;
        self.reactivateAccount = _reactivateAccount;
        self.setDefaultPaymentMethod = _setDefaultPaymentMethod;
        self.init = _init;
    };
});

